import { ComponentStyleProps } from "../../../../lib/styles/props/component-style-props";
import {
  ColorProps,
  ComponentMultiStyleConfig,
  useMultiStyleConfig,
} from "@chakra-ui/react";
import { CMSearchResultCellText } from "../CMSearchResultCellText/CMSearchResultCellText";
import { CMSearchResultCellDatetime } from "../CMSearchResultCellDatetime/CMSearchResultCellDatetime";
import React, { useMemo } from "react";
import { SearchResultCellContent } from "../types";
import { CMSearchResultCellReactRouterLink } from "../CMSearchResultCellReactRouterLink/CMSearchResultCellReactRouterLink";
import { CMSearchResultCellNotificationBadge } from "../CMSearchResultCellNotificationBadge/CMSearchResultCellNotificationBadge";
import { CMSearchResultCellTextList } from "../CMSearchResultCellTextList/CMSearchResultCellTextList";
import { PositionProps } from "@chakra-ui/styled-system";
import { isNullish } from "../../../../lib/util/common-util";
import { CMSearchResultCellReactRouterLinkList } from "../CMSearchResultCellReactRouterLinkList/CMSearchResultCellReactRouterLinkList";
import { CMSearchResultCellJsonDiff } from "../CMSearchResultCellJsonDiff";
import { SearchResultTableOptions } from "../../table";

export type CMSearchResultCellProps = {
  content: SearchResultCellContent;
  options?: SearchResultTableOptions;

  /**
   * この値を指定すると、stickyになる。
   * この値は、その際のleftの値となる。
   * 注意: カテゴリありのヘッダ(2行に亘るヘッダ)を固定列とするのは未サポート。
   */
  stickyLeft?: PositionProps["left"];
  /**
   * stickyLeftを指定してstickyにしたとき、疑似要素で左側ボーダーを作る必要がある。
   * その色。
   */
  stickyBorderLeftColor?: ColorProps["color"];
} & ComponentStyleProps;

export const CMSearchResultCell = ({
  content,
  options,

  stickyLeft,
  stickyBorderLeftColor,

  sx,
  ...rest
}: CMSearchResultCellProps) => {
  const styles = useMultiStyleConfig("CMSearchResultCellStyles");

  const stickyStyle = useMemo(() => {
    if (isNullish(stickyLeft)) return undefined;
    return {
      ...styles.sticky,
      left: stickyLeft,
      "::before": {
        ...styles.stickyBefore,
        borderLeftColor: stickyBorderLeftColor,
      },
    };
  }, [stickyLeft, styles, stickyBorderLeftColor]);

  const mergedStyles = useMemo(
    () => ({ ...styles.container, ...sx, ...stickyStyle }),
    [styles, sx, stickyStyle],
  );

  switch (content.type) {
    case "datetime":
      return (
        <CMSearchResultCellDatetime
          value={content.value}
          {...rest}
          sx={mergedStyles}
        />
      );
    case "react_router_link":
      return (
        <CMSearchResultCellReactRouterLink
          value={content.value}
          {...rest}
          sx={mergedStyles}
        />
      );
    case "react_router_link_list":
      return (
        <CMSearchResultCellReactRouterLinkList
          value={content.value}
          {...rest}
          sx={mergedStyles}
        />
      );
    case "text":
      return (
        <CMSearchResultCellText
          value={content.value}
          {...rest}
          sx={mergedStyles}
        />
      );
    case "text_list":
      return (
        <CMSearchResultCellTextList
          value={content.value}
          {...rest}
          sx={mergedStyles}
        />
      );
    case "notification_badge":
      return (
        <CMSearchResultCellNotificationBadge
          value={content.value}
          {...rest}
          sx={mergedStyles}
        />
      );
    case "json_diff":
      return (
        <CMSearchResultCellJsonDiff
          value={content.value}
          options={options?.jsonDiff}
          {...rest}
          sx={mergedStyles}
        />
      );
    default:
      throw new Error(
        `未実装の検索結果セルタイプです: ${JSON.stringify(content)}`,
      );
  }
};

export const CMSearchResultCellStyles: ComponentMultiStyleConfig = {
  parts: ["container", "sticky"],
  baseStyle: {
    container: { fontSize: "14px" },
    sticky: {
      position: "sticky",
      zIndex: 1,
      // leftはpropsから与える。
    },
    stickyBefore: {
      content: '""',
      position: "absolute",
      top: "0px",
      left: "0px",
      width: "100%",
      height: "100%",
      borderLeftWidth: "1px",
      // borderLeftColorはpropsから与える。
      // そのまま重ねるとリンク要素が有効にならないため、z-indexで調整
      zIndex: "-1",
    },
  },
};
